import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCreditCard, FiDollarSign, FiSave, FiLoader, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface PaymentSettings {
  stripe_enabled: boolean;
  crypto_enabled: boolean;
  crypto_currency: string;
  crypto_network: string;
  crypto_address: string;
}

const DEFAULT_SETTINGS: PaymentSettings = {
  stripe_enabled: true,
  crypto_enabled: false,
  crypto_currency: 'USDT',
  crypto_network: 'TRC20',
  crypto_address: '',
};

const PaymentSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [
          'payment_stripe_enabled',
          'payment_crypto_enabled',
          'payment_crypto_currency',
          'payment_crypto_network',
          'payment_crypto_address',
        ]);

      if (error) throw error;

      if (data && data.length > 0) {
        const mapped: Partial<PaymentSettings> = {};
        data.forEach((row) => {
          switch (row.setting_key) {
            case 'payment_stripe_enabled':
              mapped.stripe_enabled = row.setting_value === 'true';
              break;
            case 'payment_crypto_enabled':
              mapped.crypto_enabled = row.setting_value === 'true';
              break;
            case 'payment_crypto_currency':
              mapped.crypto_currency = row.setting_value || 'USDT';
              break;
            case 'payment_crypto_network':
              mapped.crypto_network = row.setting_value || 'TRC20';
              break;
            case 'payment_crypto_address':
              mapped.crypto_address = row.setting_value || '';
              break;
          }
        });
        setSettings({ ...DEFAULT_SETTINGS, ...mapped });
      }
    } catch (err) {
      console.error('Error fetching payment settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);

    try {
      const entries = [
        { setting_key: 'payment_stripe_enabled', setting_value: String(settings.stripe_enabled), description: 'Enable Stripe payments' },
        { setting_key: 'payment_crypto_enabled', setting_value: String(settings.crypto_enabled), description: 'Enable crypto payments' },
        { setting_key: 'payment_crypto_currency', setting_value: settings.crypto_currency, description: 'Crypto currency symbol' },
        { setting_key: 'payment_crypto_network', setting_value: settings.crypto_network, description: 'Crypto network (TRC20, ERC20, etc.)' },
        { setting_key: 'payment_crypto_address', setting_value: settings.crypto_address, description: 'Crypto wallet address' },
      ];

      for (const entry of entries) {
        const { error } = await supabase
          .from('admin_settings')
          .upsert(entry, { onConflict: 'setting_key' });
        if (error) throw error;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving payment settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FiLoader className="animate-spin text-purple-500 text-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FiDollarSign className="text-cyan-400" />
            Payment Settings
          </h2>
          <p className="text-sm text-slate-400 mt-1">Configure which payment methods are available to users</p>
        </div>
      </div>

      {/* Stripe Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <FiCreditCard className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Stripe (Card Payments)</h3>
              <p className="text-sm text-slate-400">Accept credit/debit card payments via Stripe</p>
            </div>
          </div>
          <button
            onClick={() => setSettings(s => ({ ...s, stripe_enabled: !s.stripe_enabled }))}
            className="text-3xl transition-colors"
          >
            {settings.stripe_enabled ? (
              <FiToggleRight className="text-green-400" />
            ) : (
              <FiToggleLeft className="text-slate-500" />
            )}
          </button>
        </div>
        {settings.stripe_enabled && (
          <div className="mt-4 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl">
            <p className="text-sm text-green-400">✓ Stripe is active — users will see card payment options</p>
          </div>
        )}
      </motion.div>

      {/* Crypto Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <span className="text-xl">₮</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Cryptocurrency</h3>
              <p className="text-sm text-slate-400">Accept crypto payments (USDT, USDC, etc.)</p>
            </div>
          </div>
          <button
            onClick={() => setSettings(s => ({ ...s, crypto_enabled: !s.crypto_enabled }))}
            className="text-3xl transition-colors"
          >
            {settings.crypto_enabled ? (
              <FiToggleRight className="text-green-400" />
            ) : (
              <FiToggleLeft className="text-slate-500" />
            )}
          </button>
        </div>

        {settings.crypto_enabled && (
          <div className="mt-6 space-y-4">
            {/* Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Currency</label>
                <select
                  value={settings.crypto_currency}
                  onChange={(e) => setSettings(s => ({ ...s, crypto_currency: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="USDT">USDT (Tether)</option>
                  <option value="USDC">USDC</option>
                  <option value="BTC">Bitcoin</option>
                  <option value="ETH">Ethereum</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Network</label>
                <select
                  value={settings.crypto_network}
                  onChange={(e) => setSettings(s => ({ ...s, crypto_network: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="TRC20">TRC20 (Tron)</option>
                  <option value="ERC20">ERC20 (Ethereum)</option>
                  <option value="BEP20">BEP20 (BSC)</option>
                  <option value="SOL">Solana</option>
                  <option value="BTC">Bitcoin</option>
                </select>
              </div>
            </div>

            {/* Wallet Address */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Wallet Address</label>
              <input
                type="text"
                value={settings.crypto_address}
                onChange={(e) => setSettings(s => ({ ...s, crypto_address: e.target.value }))}
                placeholder="Enter your wallet address..."
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">This address will be shown to users for crypto payments</p>
            </div>

            {!settings.crypto_address && (
              <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-sm text-amber-400">⚠ Please enter a wallet address to enable crypto payments</p>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Warning if both disabled */}
      {!settings.stripe_enabled && !settings.crypto_enabled && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-400">⚠ Both payment methods are disabled. Users won't be able to subscribe.</p>
        </div>
      )}

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? (
            <FiLoader className="w-4 h-4 animate-spin" />
          ) : (
            <FiSave className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saved && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-green-400 text-sm font-medium"
          >
            ✓ Settings saved successfully
          </motion.span>
        )}
      </div>
    </div>
  );
};

export default PaymentSettingsPanel;
